/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import CompanyButton from '@atoms/Button/CompanyButton';
import Icon from '@atoms/Icon/Icon';
import NavigationItem from '@atoms/NavigationItem/NavigationItem';
import useLocale from '@hooks/useLocale';
import LanguageSwitch from '@molecules/LanguageSwitch/LanguageSwitch';
import { Menu as MenuType, SolutionsMenuItem } from '@type-declarations/menu';
import isSolutionsMainMenuItem from '@utils/isSolutionsMenuItem';
import isValidLink from '@utils/isValidLink';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

import styles from './Menu.module.scss';

interface Props {
  mainMenu: MenuType;
  menuIsShown: boolean;
  currentPageId?: string;
  id: string;
  solutionsMenuItems?: SolutionsMenuItem[];
}

export default function Menu({
  mainMenu,
  menuIsShown,
  currentPageId,
  id,
  solutionsMenuItems = [],
}: Props) {
  const locale = useLocale();
  const primaryMenu = mainMenu.primaryMenu.filter(isValidLink);
  const secondaryMenu = mainMenu.secondaryMenu.filter(isValidLink);

  const [subMenuOpen, setSubMenuOpen] = useState(false);

  const subMenuItems = solutionsMenuItems.filter(isValidLink);

  return (
    <div className={clsx(styles.menu, menuIsShown && styles.isOpen)} id={id}>
      <div className={clsx(styles.container, 'u-container')}>
        <ul className={clsx(styles.primaryContainer, 'u-list-clean')}>
          {primaryMenu.map(menuItem => {
            const linkPageID = menuItem.internalLink?.page[0]?.id;
            const isActive = linkPageID === currentPageId;
            const hasSubMenuItems =
              isSolutionsMainMenuItem(menuItem, locale) &&
              subMenuItems.length > 0;

            return (
              <li
                className={clsx(
                  styles.item,
                  isActive && styles.active,
                  hasSubMenuItems && styles.subMenuItem,
                )}
                key={menuItem.id}
              >
                {hasSubMenuItems ? (
                  <>
                    <div className={styles.menuItemContainer}>
                      <Icon
                        icon="arrow-right"
                        className={styles.icon}
                        aria-hidden
                      />
                      <button
                        className={styles.subMenuHeader}
                        onClick={() => setSubMenuOpen(!subMenuOpen)}
                        type="button"
                        aria-expanded={subMenuOpen}
                        aria-controls="sub-menu-dropdown"
                      >
                        <span className={styles.menuItem}>
                          {menuItem.title}
                        </span>
                        <Icon
                          icon="chevron-down"
                          className={clsx(
                            styles.dropdownIcon,
                            subMenuOpen && styles.dropdownIconOpen,
                          )}
                          aria-hidden
                        />
                      </button>
                    </div>

                    <AnimatePresence>
                      {subMenuOpen && (
                        <motion.div
                          className={styles.subMenuDropdown}
                          id="sub-menu-dropdown"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          style={{ overflow: 'hidden' }}
                        >
                          {subMenuItems.map(item => (
                            <div
                              key={item.id}
                              className={styles.subMenuItemContent}
                            >
                              <div className={styles.subMenuContent}>
                                {item.internalLink || item.externalLink ? (
                                  <NavigationItem
                                    item={item}
                                    className={styles.subMenuTitle}
                                  >
                                    {item.title}
                                  </NavigationItem>
                                ) : (
                                  <div className={styles.subMenuTitle}>
                                    {item.title}
                                  </div>
                                )}

                                {(item.company || item.secondCompany) && (
                                  <div className={styles.subMenuButtons}>
                                    <CompanyButton
                                      name={item.company}
                                      internalLink={item.companyInternalLink}
                                      externalLink={item.companyExternalLink}
                                    />
                                    <CompanyButton
                                      name={item.secondCompany}
                                      internalLink={
                                        item.secondCompanyInternalLink
                                      }
                                      externalLink={
                                        item.secondCompanyExternalLink
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <div className={styles.menuItemContainer}>
                    <Icon
                      icon="arrow-right"
                      className={styles.icon}
                      aria-hidden
                    />
                    <NavigationItem item={menuItem} className={styles.menuItem}>
                      {menuItem.title}
                    </NavigationItem>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <div className={styles.secondaryContainer}>
          <ul className={clsx(styles.secondaryList, 'u-list-clean')}>
            {secondaryMenu.map(menuItem => {
              const linkPageID = menuItem.internalLink?.page[0]?.id;
              const isActive = linkPageID === currentPageId;

              return (
                <li
                  className={clsx(styles.subItem, isActive && styles.active)}
                  key={menuItem.id}
                >
                  <div className={styles.subMenuItemContainer}>
                    <Icon
                      icon="arrow-right"
                      className={styles.subIcon}
                      aria-hidden
                    />
                    <NavigationItem
                      item={menuItem}
                      className={styles.secondaryItem}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
          <div className={styles.languageSwitchContainer}>
            <LanguageSwitch />
          </div>
        </div>
      </div>
    </div>
  );
}
